import { useTranslation } from "react-i18next";
import layoutStyles from "../layout/SectionLayout.module.css";
import { ViewOptions } from "../layout/ViewOptions";
import { requestViews, type RequestView } from "./requestView";

/** The icon for each view: stacked tiles, or a table's rows and columns. */
function ViewIcon({ view }: { view: RequestView }): React.JSX.Element {
  return (
    <svg className={layoutStyles.icon} viewBox="0 0 16 16" aria-hidden="true">
      {view === "tiles" ? (
        <>
          <rect x="1" y="1.5" width="14" height="5.5" rx="1.25" />
          <rect x="1" y="9" width="14" height="5.5" rx="1.25" />
        </>
      ) : (
        <>
          <rect x="1" y="1.5" width="4" height="3" rx="0.5" />
          <rect x="6" y="1.5" width="9" height="3" rx="0.5" />
          <rect x="1" y="6.5" width="4" height="3" rx="0.5" />
          <rect x="6" y="6.5" width="9" height="3" rx="0.5" />
          <rect x="1" y="11.5" width="4" height="3" rx="0.5" />
          <rect x="6" y="11.5" width="9" height="3" rx="0.5" />
        </>
      )}
    </svg>
  );
}

/**
 * The My Requests view controls (`ViewOptions`), floating at the top of the page as the other pages' do: the "show completed"
 * switch, then the choice between the tiles and the table, which closes the pill once made.
 *
 * @param props.view - The view in use.
 * @param props.onChange - Called with the view the person picks.
 * @param props.showCompleted - Whether finished requests are shown.
 * @param props.onShowCompletedChange - Called with the new "show completed" setting.
 */
export function RequestViewSwitch({
  view,
  onChange,
  showCompleted,
  onShowCompletedChange,
}: {
  view: RequestView;
  onChange: (view: RequestView) => void;
  showCompleted: boolean;
  onShowCompletedChange: (show: boolean) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const labels: Record<RequestView, string> = {
    tiles: t("myRequests.view.tiles", "Tiles"),
    table: t("myRequests.view.table", "Table"),
  };

  return (
    <ViewOptions>
      {(close) => (
        <>
          <button type="button" className={layoutStyles.option} aria-pressed={showCompleted} onClick={() => onShowCompletedChange(!showCompleted)}>
            <svg className={layoutStyles.icon} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
              <path d="M3 8.5 6.5 12 13 4.5" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>{t("myRequests.showCompleted", "Show Completed Requests")}</span>
          </button>
          <div className={layoutStyles.group} role="group" aria-label={t("myRequests.view.label", "Show requests as")}>
            {requestViews.map((option) => (
              <button
                key={option}
                type="button"
                className={layoutStyles.option}
                aria-pressed={view === option}
                onClick={() => {
                  onChange(option);
                  close();
                }}
              >
                <ViewIcon view={option} />
                <span>{labels[option]}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </ViewOptions>
  );
}
